/**
 * Gleicher Abstand — das Einrasten, das Figma, FigJam und Miro „equal spacing"
 * nennen.
 *
 * Zwei Formen derselben Notiz:
 *
 * 1. **Dazwischen** — eine Notiz zwischen zwei anderen bekommt links und
 *    rechts dieselbe Lücke.
 * 2. **Die Reihe fortsetzen** — zwei Notizen mit bekanntem Abstand geben den
 *    Takt vor, ein dritter rastet außen im selben Abstand ein.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`: die Geometrie soll ohne
 * gerenderten Canvas prüfbar sein.
 */

import type { Bounds, Measure, MovingRect } from './canvasAlignment'

export interface SpacingCandidate {
  /** Die Position, die das bewegte Element bekäme (linke bzw. obere Kante). */
  position: number
  /** Abstand zur aktuellen Lage — kleiner ist besser. */
  distance: number
  /**
   * Die beiden Lücken, die als gleich angezeigt werden. Immer ein Paar: Die
   * Aussage ist ja, dass sie übereinstimmen.
   */
  measures: Measure[]
}

/** Die Achsenwerte eines Rechtecks, je nachdem welche Achse gerade zählt. */
interface AxisView {
  /** Führende Kante auf der Mess-Achse (links bzw. oben). */
  lead: number
  /** Folgende Kante (rechts bzw. unten). */
  trail: number
  /** Ausdehnung auf der ANDEREN Achse — dort muss überlappt werden. */
  crossStart: number
  crossEnd: number
}

const viewOf = (axis: 'x' | 'y') => (b: Bounds): AxisView =>
  axis === 'x'
    ? { lead: b.left, trail: b.right, crossStart: b.top, crossEnd: b.bottom }
    : { lead: b.top, trail: b.bottom, crossStart: b.left, crossEnd: b.right }

/**
 * Kandidaten für den gleichen Abstand auf EINER Achse.
 *
 * Gefiltert wird zuerst auf Nachbarn, die auf der **anderen** Achse überlappen
 * — für den waagerechten Abstand also solche, die senkrecht auf gleicher Höhe
 * liegen. Ohne diese Bedingung wäre „gleicher Abstand" eine Aussage über
 * nichts: Die Klammern lägen im Leeren und die Notiz rastete an einer Stelle
 * ein, die niemand gemeint hat.
 *
 * Zurück kommen ALLE Kandidaten, ungefiltert nach Nähe — die Fangschwelle
 * entscheidet der Aufrufer (`findAxisAlignment`), damit sie an einer Stelle
 * liegt.
 */
export function getSpacingCandidates(
  moving: MovingRect,
  others: Bounds[],
  axis: 'x' | 'y',
): SpacingCandidate[] {
  const view = viewOf(axis)
  const lead = axis === 'x' ? moving.x : moving.y
  const size = axis === 'x' ? moving.width : moving.height
  const crossStart = axis === 'x' ? moving.y : moving.x
  const crossEnd = crossStart + (axis === 'x' ? moving.height : moving.width)
  // Die Klammern sitzen auf der Mittellinie des bewegten Notiz. Immer
  // dieselbe Höhe, egal wie die Nachbarn stehen — so liegen sie dort, wo man
  // ohnehin hinsieht.
  const at = crossStart + (crossEnd - crossStart) / 2

  // Nur echte Überlappung zählt: Bündig aneinanderstoßende Bänder (`>=`)
  // hießen, dass zwei untereinanderliegende Notizen als „nebeneinander"
  // gälten.
  const neighbours = others
    .map(view)
    .filter(o => o.crossEnd > crossStart && o.crossStart < crossEnd)
    .sort((a, b) => a.lead - b.lead)

  if (neighbours.length < 2) return []

  const gap = (from: number, to: number): Measure => ({ axis, from, to, at, kind: 'gap' })
  const candidates: SpacingCandidate[] = []
  const add = (position: number, measures: Measure[]) => {
    candidates.push({ position, distance: Math.abs(lead - position), measures })
  }

  // Nur BENACHBARTE Paare: Zwischen zwei Notizen, die selbst schon einen
  // dritten zwischen sich haben, ist „gleicher Abstand" zwar rechnerisch
  // möglich, aber niemand meint das.
  for (let i = 0; i < neighbours.length - 1; i++) {
    const left = neighbours[i]!
    const right = neighbours[i + 1]!
    const between = right.lead - left.trail
    if (between <= 0) continue

    // 1. Dazwischen — es gibt genau eine Lösung, nichts zu wählen.
    const inner = (between - size) / 2
    if (inner > 0) {
      const position = left.trail + inner
      add(position, [
        gap(left.trail, position),
        gap(position + size, right.lead),
      ])
    }

    // 2. Die Reihe fortsetzen. Zwei Notizen genügen als Takt: Sie definieren
    // bereits einen Abstand, und ein dritter im selben Abstand ist genau das,
    // was man erwartet.
    //
    // Die vorgebende Lücke wird MIT angezeigt — sonst behauptete die einzelne
    // Klammer einen Rhythmus, den man nicht sieht.
    const reference = gap(left.trail, right.lead)

    const after = right.trail + between
    add(after, [reference, gap(right.trail, after)])

    const before = left.lead - between - size
    add(before, [gap(before + size, left.lead), reference])
  }

  return candidates
}
